/**
 * DaddyTalkChatPreview — /daddytalk/chat/:recruiterId
 *
 * The anonymous chat composer. Any visitor can land here from the
 * recruiter directory, see the recruiter's public profile, type a
 * message freely, and only get asked for identity when they click
 * SEND. Draft persistence: message survives page reloads via
 * localStorage keyed by recruiter id.
 *
 * After signup (via InlineSignupModal → magic link OR email+password),
 * the drafted message is picked back up and queued for delivery.
 * Actual chat_threads persistence lands in M2 — for now we stash the
 * intent as `daddytalk_pending_send` in localStorage so M2 can replay
 * it on first authed hit.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import SeoMeta from "@/components/SeoMeta";
import InlineSignupModal from "@/components/InlineSignupModal";
import {
    ArrowLeft,
    PaperPlaneTilt,
    CheckCircle,
    ChatCircleText,
    Clock,
    ShieldCheck,
} from "@phosphor-icons/react";

// Default workspace for the public directory lookup. When we go multi-
// workspace directory, this needs to expand to search across tenants.
const DEFAULT_WORKSPACE = "ws_default_0000000000000000";

const MAX_MSG_LEN = 1200;
const DRAFT_KEY = (rid) => `daddytalk_draft_${rid}`;
const PENDING_SEND_KEY = "daddytalk_pending_send";

export default function DaddyTalkChatPreview() {
    const { recruiterId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAuthed = !!user && typeof user === "object";

    const [recruiter, setRecruiter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(() => {
        try { return localStorage.getItem(DRAFT_KEY(recruiterId)) || ""; }
        catch { return ""; }
    });
    const [signupOpen, setSignupOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    // Persist draft to localStorage on every change so a reload / login
    // round-trip doesn't lose the message.
    useEffect(() => {
        try {
            if (message) localStorage.setItem(DRAFT_KEY(recruiterId), message);
            else localStorage.removeItem(DRAFT_KEY(recruiterId));
        } catch { /* ignore */ }
    }, [message, recruiterId]);

    // Fetch recruiter details from the public directory. If not found /
    // no longer eligible, show a graceful "offline" message.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(
                    `/public/daddytalk/eligible-recruiters?workspace_id=${DEFAULT_WORKSPACE}&limit=100`,
                );
                if (cancelled) return;
                const match = (data?.items || []).find((r) => r.id === recruiterId);
                setRecruiter(match || null);
            } catch {
                if (!cancelled) setRecruiter(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [recruiterId]);

    // If the user just came back from a magic-link signup, replay the
    // pending message they were trying to send.
    //
    // SEC-003 (V1.6.7 audit): (a) enforce a 10-minute max-age on the stash
    // via the `t` timestamp so a device attacker can't plant a draft that
    // survives days later, (b) require explicit user confirmation before
    // sending — no silent auto-dispatch — so a same-origin script planting
    // localStorage can't send messages as the victim.
    useEffect(() => {
        if (!isAuthed) return;
        let pending = null;
        try { pending = JSON.parse(localStorage.getItem(PENDING_SEND_KEY) || "null"); }
        catch { pending = null; }
        if (!pending || pending.recruiterId !== recruiterId || !pending.body) return;

        // Reject stashes older than 10 minutes — likely stale / planted.
        const MAX_STASH_AGE_MS = 10 * 60 * 1000;
        const ageMs = pending.t ? (Date.now() - Number(pending.t)) : Infinity;
        if (!Number.isFinite(ageMs) || ageMs > MAX_STASH_AGE_MS) {
            try { localStorage.removeItem(PENDING_SEND_KEY); } catch { /* ignore */ }
            return;
        }

        // Pre-fill the composer + toast a review prompt — never auto-send.
        setMessage(pending.body);
        try { localStorage.removeItem(PENDING_SEND_KEY); } catch { /* ignore */ }
        toast.info("Review your message and tap Send to deliver it.", { duration: 6000 });
    }, [isAuthed, recruiterId]);

    const charsLeft = MAX_MSG_LEN - message.length;
    const canSubmit = useMemo(
        () => message.trim().length >= 10 && charsLeft >= 0 && !sending,
        [message, charsLeft, sending],
    );

    async function doSend(bodyToSend) {
        const body = (bodyToSend ?? message).trim();
        if (!body) return;
        setSending(true);
        try {
            // V1.6.0 M2 — real chat_threads API. Backend dedupes on
            // (initiator, target, status != closed) so a double-tap
            // won't create ghost threads.
            const { data } = await api.post("/daddytalk/threads", {
                recruiter_id: recruiterId,
                body,
                context_type: null,
                context_id: null,
            });
            // Clear draft + pending stash — the message is now persisted.
            try { localStorage.removeItem(DRAFT_KEY(recruiterId)); } catch { /* ignore */ }
            try { localStorage.removeItem(PENDING_SEND_KEY); } catch { /* ignore */ }
            setSent(true);
            toast.success(
                data?.reused
                    ? "Follow-up delivered to your existing thread."
                    : "Message delivered — recruiter has been notified.",
            );
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409) {
                toast.error(detail || "Recruiter isn't accepting chats right now.");
            } else if (status === 429) {
                toast.error(detail || "You're sending too many chats — try again later.");
            } else {
                toast.error(detail || "Couldn't send the message. Please try again.");
            }
        } finally {
            setSending(false);
        }
    }

    function handleSendClick() {
        if (!canSubmit) return;
        if (!isAuthed) {
            // Stash the intent so post-signup the chat replays it.
            try {
                localStorage.setItem(
                    PENDING_SEND_KEY,
                    JSON.stringify({ recruiterId, body: message.trim(), t: Date.now() }),
                );
            } catch { /* ignore */ }
            setSignupOpen(true);
            return;
        }
        doSend();
    }

    const recruiterName = recruiter?.name || "The recruiter";

    return (
        <div className="min-h-screen bg-cream">
            <SeoMeta
                title={`Chat with ${recruiterName} on DaddyTalk`}
                description="Draft your message. Sign in only when you're ready to send."
                canonical={`/daddytalk/chat/${recruiterId}`}
            />

            {/* Nav */}
            <header className="border-b border-ink/10 bg-white">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link
                        to="/daddytalk"
                        data-testid="daddytalk-chat-back"
                        className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-[0.2em] text-ink/60 hover:text-ink transition-colors"
                    >
                        <ArrowLeft size={12} weight="bold" />
                        Back to directory
                    </Link>
                    <Brand size="sm" />
                </div>
            </header>

            {loading ? (
                <div className="max-w-3xl mx-auto px-4 py-16 text-center text-ink/60">
                    Loading recruiter…
                </div>
            ) : !recruiter ? (
                // Recruiter went offline / doesn't exist / workspace disabled
                <div className="max-w-2xl mx-auto px-4 py-16 text-center">
                    <img
                        src="/mascot/daddytalk/avatar_thinking.png"
                        alt=""
                        className="w-28 h-28 rounded-full mx-auto mb-4 shadow-md"
                    />
                    <h1 className="font-heading text-2xl font-black mb-2">
                        Recruiter isn't available right now
                    </h1>
                    <p className="text-ink/60 text-sm mb-6">
                        They may have paused new chats or gone offline. Try someone else in the
                        live directory, or apply directly to an open role.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                        <Link
                            to="/daddytalk"
                            data-testid="daddytalk-chat-empty-directory"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-cobalt text-white text-[11px] font-black uppercase tracking-[0.2em] hover:bg-cobalt-dark transition-colors"
                        >
                            Back to directory
                        </Link>
                        <Link
                            to="/jobs"
                            data-testid="daddytalk-chat-empty-jobs"
                            className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-ink text-ink text-[11px] font-black uppercase tracking-[0.2em] hover:bg-ink hover:text-white transition-colors"
                        >
                            Browse jobs
                        </Link>
                    </div>
                </div>
            ) : sent ? (
                // Success screen
                <div
                    className="max-w-2xl mx-auto px-4 py-16 text-center"
                    data-testid="daddytalk-chat-sent-screen"
                >
                    <img
                        src="/mascot/daddytalk/avatar_celebrating.png"
                        alt=""
                        className="w-32 h-32 rounded-full mx-auto mb-5 shadow-md"
                    />
                    <h1 className="font-heading text-3xl font-black mb-3">
                        Message queued!
                    </h1>
                    <p className="text-ink/70 text-base mb-2 max-w-lg mx-auto">
                        Uncle D will deliver your note to <strong>{recruiterName}</strong> the
                        moment they come online.
                    </p>
                    <p className="text-xs text-ink/50 mb-8 flex items-center gap-1.5 justify-center">
                        <Clock size={12} weight="bold" />
                        Typical response time: under 2 hours during working hours
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                        <Link
                            to="/daddytalk"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-cobalt text-white text-[11px] font-black uppercase tracking-[0.2em] hover:bg-cobalt-dark transition-colors"
                        >
                            Message another recruiter
                        </Link>
                        <Link
                            to="/candidate"
                            className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-ink text-ink text-[11px] font-black uppercase tracking-[0.2em] hover:bg-ink hover:text-white transition-colors"
                        >
                            Go to my dashboard
                        </Link>
                    </div>
                </div>
            ) : (
                // Main composer
                <div className="max-w-3xl mx-auto px-4 py-8">
                    {/* Recruiter card */}
                    <div
                        data-testid="daddytalk-chat-recruiter-card"
                        className="bg-white border-2 border-ink/10 p-4 sm:p-5 flex items-center gap-4 mb-6"
                    >
                        <div className="relative shrink-0">
                            {recruiter.avatar_url ? (
                                <img
                                    src={recruiter.avatar_url}
                                    alt={recruiter.name || "Recruiter"}
                                    data-testid="daddytalk-chat-recruiter-avatar"
                                    className="w-16 h-16 rounded-full object-cover border-4 border-cream"
                                    onError={(e) => { e.currentTarget.src = "/mascot/daddytalk/avatar_neutral.png"; }}
                                />
                            ) : (
                                <div
                                    data-testid="daddytalk-chat-recruiter-avatar-initial"
                                    className="w-16 h-16 rounded-full border-4 border-cream bg-gradient-to-br from-cobalt to-cobalt/70 text-white flex items-center justify-center font-heading font-black text-2xl"
                                >
                                    {(recruiter.name || "R").trim().charAt(0).toUpperCase()}
                                </div>
                            )}
                            <span
                                className="absolute bottom-0 right-0 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white"
                                aria-label="Available"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="font-heading text-xl font-black truncate">
                                {recruiter.name}
                            </h1>
                            <p className="text-xs text-ink/50 uppercase tracking-wider">
                                {recruiter.role === "hiring_manager" ? "Hiring Manager" : "Recruiter"}
                                <span className="mx-2">·</span>
                                <span className="text-emerald-600 font-semibold">
                                    Accepting chats now
                                </span>
                            </p>
                        </div>
                    </div>

                    {/* Uncle D welcome bubble */}
                    <div
                        data-testid="daddytalk-chat-uncle-d-bubble"
                        className="flex items-start gap-3 mb-6"
                    >
                        <img
                            src="/mascot/daddytalk/avatar_hello.png"
                            alt="Uncle D"
                            className="w-10 h-10 rounded-full shrink-0"
                        />
                        <div className="bg-white border border-ink/10 px-4 py-3 max-w-xl rounded-r-2xl rounded-tl-2xl text-sm text-ink/80 shadow-sm">
                            <p className="mb-1">
                                <span className="font-black text-cobalt">Uncle D:</span>{" "}
                                Hi! Draft your message to <strong>{recruiterName}</strong> —
                                introduce yourself, mention the role you're interested in,
                                and add any highlights that'll grab their attention.
                            </p>
                            {!isAuthed && (
                                <p className="text-xs text-ink/50 mt-2">
                                    <ShieldCheck
                                        size={12}
                                        weight="bold"
                                        className="inline-block text-emerald-500 mr-1"
                                    />
                                    Type freely — we'll only ask for your details when you hit send.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Composer */}
                    <div className="bg-white border-2 border-ink/10 focus-within:border-cobalt transition-colors">
                        <textarea
                            data-testid="daddytalk-chat-composer"
                            value={message}
                            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MSG_LEN))}
                            rows={8}
                            placeholder={`Hi ${recruiter.name.split(" ")[0]}, I'm a ${'{{your role}}'} with ${'{{years}}'} years experience at ${'{{company}}'}. I saw you're hiring — I'd love to know more about the opportunity…`}
                            className="w-full px-4 py-4 text-sm text-ink resize-none outline-none placeholder:text-ink/30"
                        />
                        <div className="flex items-center justify-between px-4 py-2.5 border-t border-ink/10 bg-cream">
                            <p
                                data-testid="daddytalk-chat-char-count"
                                className={`text-[10px] uppercase tracking-wider font-black ${
                                    charsLeft < 100 ? "text-red-500" : "text-ink/40"
                                }`}
                            >
                                {message.trim().length} / {MAX_MSG_LEN}
                            </p>
                            <button
                                type="button"
                                onClick={handleSendClick}
                                disabled={!canSubmit}
                                data-testid="daddytalk-chat-send"
                                className={`btn-gradient inline-flex items-center gap-2 px-5 py-2 text-[11px] font-black uppercase tracking-[0.2em] disabled:opacity-40 disabled:cursor-not-allowed`}
                            >
                                {sending ? "Sending…" : isAuthed ? "Send message" : "Send"}
                                <PaperPlaneTilt size={12} weight="bold" />
                            </button>
                        </div>
                    </div>

                    {/* Tips */}
                    <div className="mt-6 grid sm:grid-cols-3 gap-3">
                        {[
                            { icon: CheckCircle, text: "Be specific — mention the role you saw." },
                            { icon: ChatCircleText, text: "Keep it 2–4 sentences. Save details for the chat." },
                            { icon: Clock, text: "Most recruiters reply within 2 hours." },
                        ].map(({ icon: Icon, text }, i) => (
                            <div
                                key={i}
                                className="flex items-start gap-2 text-xs text-ink/60"
                            >
                                <Icon
                                    size={14}
                                    weight="bold"
                                    className="text-cobalt shrink-0 mt-0.5"
                                />
                                {text}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Inline signup modal — fires only on Send when unauthed */}
            <InlineSignupModal
                open={signupOpen}
                onClose={() => setSignupOpen(false)}
                contextLine={`So ${recruiterName} can reply to your message.`}
                preservedDraft={message.trim()}
                onAuthed={() => {
                    setSignupOpen(false);
                    // After password-signup the cookie is set immediately —
                    // fire the send. Magic-link path completes on the /candidate/*
                    // route and lands back here via useAuth reload.
                    doSend();
                }}
            />
        </div>
    );
}
